import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { NoObjectChosen } from "../../common/NoObjectChosen";
import { selectFolderItems } from "../../common/Sidebar/redux/selectors";
import { toggleAccordion } from "../../common/Sidebar/redux/slice";
import {
  classificatorApiUrl,
  classificatorFolderId,
  classificatorKey,
  classificatorModel,
} from "../../services/api/instances/classificatorApi/constants";
import type {
  ClassifierValue,
  ClassPostObjType,
  Label,
  PredictionObject,
} from "../../services/api/instances/classificatorApi/types";
import { createStorageObjectApi } from "../../services/lib/storageObjectManager";
import { getServicePagePath } from "../../services/navigation/navigateToRoute";
import { useAppDispatch } from "../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../services/redux/hooks/use-selector";
import { ClassLeftContainer } from "./ClassLeftContainer";
import { ClassRightContainer } from "./ClassRightContainer";
import { useClassifier } from "./hooks/use-classifier";
import {
  createClassifierAction,
  getClassesAction,
  updateClassifierWithChildrenAction,
  updateStorageObjectAction,
} from "./redux/actions/actions";
import {
  selectClassifierResults,
  selectClassifiers,
  selectSelectedClassifier,
} from "./redux/selectors";
import {
  setClassifierResultsAction,
  setClassifiersAction,
  setSelectedClassifierAction,
} from "./redux/slice";
import styles from "./styles.module.css";
import { StorageObject } from "../../services/api/instances/storageApi/types/object_types";

export const getRefTextForJson = (ref?: string) => {
  if (!ref) {
    return "";
  }
  return `${ref.slice(0, 6)}*************${ref.slice(-6)}`;
};

export const Classification = () => {
  const dispatch = useAppDispatch();
  const classifiers = useAppSelector(selectClassifiers);
  const classifierResult = useAppSelector(selectClassifierResults);
  const selectedClassifier = useAppSelector(selectSelectedClassifier);
  const folderItems = useAppSelector(selectFolderItems);
  const [isLoading, setIsLoading] = useState(false);
  const { onNameChange } = useClassifier();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const storage = createStorageObjectApi<ClassifierValue>(classifiers);

  const addClassifier = () => {
    dispatch(createClassifierAction({ classifierType: "copy" }))
      .unwrap()
      .then((response: StorageObject<ClassifierValue>) => {
        dispatch(
          updateClassifierWithChildrenAction({
            childId: response.id,
            parentId: selectedClassifier?.id as string,
          }),
        );
        const newClassifiers = storage.addItem(
          response,
          selectedClassifier?.id,
        );
        dispatch(setClassifiersAction(newClassifiers));
        dispatch(setSelectedClassifierAction(response));
        dispatch(setClassifierResultsAction(null));
        navigate(getServicePagePath(response.id, "classify"));
        if (!folderItems.includes(selectedClassifier?.id as string)) {
          dispatch(toggleAccordion(selectedClassifier?.id as string));
        }
      });
  };

  const cleanLabelsAndSamples = (labels: Label[] = []): Label[] => {
    return labels.map((label) => {
      const { id, ...restLabel } = label;
      return {
        ...restLabel,
        samples: restLabel.samples.map((sample) => {
          const { id, ...restSample } = sample;

          return restSample.value as any;
        }),
      };
    });
  };

  const getRequestData = () => {
    return {
      prefix: selectedClassifier?.value?.classifier_object?.prefix,
      labels: cleanLabelsAndSamples(
        selectedClassifier?.value?.classifier_object?.labels,
      ),
      text_request: selectedClassifier?.value?.classifier_object?.text_request,
      folder_id: classificatorFolderId,
      api_key: classificatorKey,
      model: classificatorModel,
    };
  };

  const classifierUpdate = (postObj: StorageObject<ClassifierValue>) => {
    dispatch(updateStorageObjectAction(postObj));
  };

  const handleClassification = () => {
    setIsLoading(true);
    const requestData = getRequestData();

    dispatch(getClassesAction(requestData as ClassPostObjType))
      .unwrap()
      .then((response: PredictionObject) => {
        dispatch(setClassifierResultsAction(response));
      })
      .catch(console.error)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getRequestBody = () => {
    const requestData = getRequestData();

    return `
      curl -X POST ${classificatorApiUrl}
       -H "Authorization: Bearer XXXXX"
       -H "Content-Type: application/json"
       -d '{"ref": "${getRefTextForJson(selectedClassifier?.ref)}", "text": "${
         requestData.text_request || ""
       }" }'
    `;
  };

  const getRequestBodyForCopy = () => {
    const requestData = getRequestData();

    return `
      curl -X POST ${classificatorApiUrl}
       -H "Authorization: Bearer XXXXX"
       -H "Content-Type: application/json"
       -d '{"ref": "${selectedClassifier?.ref}", "text": "${
         requestData.text_request || ""
       }" }'
    `;
  };

  return (
    <div className={styles["layout-container"]}>
      {selectedClassifier?.value === null ? (
        <NoObjectChosen
          isNameChanged
          message={t("classification.no_object_message")}
          folderName={selectedClassifier.friendly_name}
          onClick={addClassifier}
        />
      ) : (
        <>
          <ClassLeftContainer
            updateClassifier={classifierUpdate}
            leftData={
              selectedClassifier?.value.classifier_object as ClassPostObjType
            }
          />

          <ClassRightContainer
            data={classifierResult}
            leftData={
              (
                selectedClassifier?.value.classifier_object as {
                  labels: Label[];
                }
              ).labels
            }
            isLoading={isLoading}
            onClassification={handleClassification}
            requestBody={getRequestBody}
            getRequestBodyForCopy={getRequestBodyForCopy}
          />
        </>
      )}
    </div>
  );
};
